import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { cn } from "@/lib/utils";

interface PaginationProps {
  currentPage: number;
  lastPage: number;
  onPageChange: (page: number) => void;
}

const CustomPaginationComponent: React.FC<PaginationProps> = ({
  currentPage,
  lastPage,
  onPageChange,
}) => {
  const canGoPrevious = currentPage > 0;
  const canGoNext = currentPage < lastPage - 1;

  const renderPageNumbers = () => {
    const pageNumbers: (number | "ellipsis")[] = [];
    const delta = 2;
    let startPage = Math.max(currentPage - delta, 0);
    let endPage = Math.min(currentPage + delta, lastPage - 1);

    // Always show first page if not in range
    if (startPage > 1) {
      pageNumbers.push(0);
      if (startPage > 2) {
        pageNumbers.push("ellipsis");
      }
    }

    // Show pages in range
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    // Always show last page if not in range
    if (endPage < lastPage - 1) {
      if (endPage < lastPage - 2) {
        pageNumbers.push("ellipsis");
      }
      pageNumbers.push(lastPage - 1);
    }

    return pageNumbers;
  };

  const handlePrevious = (e: React.MouseEvent) => {
    e.preventDefault();
    if (canGoPrevious) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (canGoNext) {
      onPageChange(currentPage + 1);
    }
  };

  const handlePageClick = (page: number) => (e: React.MouseEvent) => {
    e.preventDefault();
    if (page >= 0 && page < lastPage && page !== currentPage) {
      onPageChange(page);
    }
  };

  // Don't render pagination if there's only one page or no pages
  if (lastPage <= 1) {
    return null;
  }

  return (
    <Pagination>
      <PaginationContent className="mb-4">
        <PaginationItem>
          <PaginationPrevious
            href={canGoPrevious ? `?page=${currentPage - 1}` : "#"}
            onClick={handlePrevious}
            className={cn(
              !canGoPrevious && "pointer-events-none opacity-50"
            )}
            aria-disabled={!canGoPrevious}
          />
        </PaginationItem>

        {renderPageNumbers().map((page, index) => (
          <PaginationItem key={`${page}-${index}`}>
            {page === "ellipsis" ? (
              <PaginationEllipsis />
            ) : (
              <PaginationLink
                href={`?page=${page}`}
                onClick={handlePageClick(page)}
                isActive={page === currentPage}
              >
                {(page as number) + 1}
              </PaginationLink>
            )}
          </PaginationItem>
        ))}

        <PaginationItem>
          <PaginationNext
            href={canGoNext ? `?page=${currentPage + 1}` : "#"}
            onClick={handleNext}
            className={cn(
              !canGoNext && "pointer-events-none opacity-50"
            )}
            aria-disabled={!canGoNext}
          />
        </PaginationItem>
      </PaginationContent>
    </Pagination>
  );
};

export default CustomPaginationComponent;
